<template>
  <AddTaskButton
    v-if="canAddTask && !isInPast"
    ref="addTaskButtonEl"
    class="add-task"
    :class="{
      // Handle column (16px) when reorderable; otherwise the legacy
      // 32px gutter used when rows had a star column.
      star: !isInPast && !(listKey && allowReorder),
      reorderable: !isInPast && !!listKey && allowReorder,
    }"
    :add-task-action="effectiveAddTaskAction"
    :post-validate-action="selectTaskById"
    :metrics-source="'agenda/task-list'"
    @focus="unselectTask"
  />
  <p v-if="displayedItems.length === 0 && emptyText" class="empty">
    {{ emptyText }}
  </p>
  <div
    ref="taskListEl"
    v-hotkey="shortcuts"
    v-click-outside="unselectTask"
    class="itemslist"
    :class="{
      canAddTask: canAddTask && !isInPast,
      // Spacer/handle replaces the star column; drop the 32px that used to.
      reorderable: !!listKey && allowReorder,
    }"
    @click.exact.self="unselectTask"
  >
    <Ordering
      :list-key="listKey"
      :ids="displayedItems"
      :disabled="isEditing"
      :reorderable="!!listKey && allowReorder"
      :managed="false"
      @reorder="onReorder"
    >
      <template #item="{ id }">
        <TaskInlineDraggable
          :reorderable="!!listKey && allowReorder"
          :actions="taskRowActions"
          :class="{ selected: id === selectedTaskId }"
          :is-selected="id === selectedTaskId"
          :is-editing="isEditing"
          :task-id="id"
          :hide-scheduled-date="hideScheduledDate"
          @select="selectTaskById(id)"
          @set-selected-task-el="setSelectedTaskEl"
          @quit-editing="quitEditing"
        />
      </template>
    </Ordering>
    <div
      v-if="displayedItems.length > 0"
      key="bottom"
      class="bottom"
      @click="unselectTask"
    />
  </div>

  <div
    v-if="showLoadMore"
    ref="infiniteScrollTrigger"
    class="infinite-scroll-trigger"
  >
    <div v-if="isLoadingMore" class="loading-more">
      <Spinner />
      <span>Loading more tasks...</span>
    </div>
  </div>
</template>

<script lang="ts">
import { isAfter, startOfDay } from "date-fns";
import { debounce } from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import type {
  ConsoleActionTask,
  GeneralizedTaskId,
} from "@ctrl/controller-rpc";

import AddTaskButton from "@/components/AddTaskButton.vue";
import TaskInlineDraggable from "@/components/agenda/TaskInlineDraggable.vue";
import * as inject from "@/components/inject";
import { TaskActions } from "@/components/task/types";
import { useOrderableTaskList } from "@/components/tasks/ordering";
import Ordering from "@/components/tasks/Ordering.vue";
import { useReleaser } from "@/ref";

import { useTaskList } from "../task/useTaskList";

export default defineComponent({
  name: "TaskList",
  components: {
    AddTaskButton,
    Ordering,
    TaskInlineDraggable,
  },
  props: {
    canAddTask: { type: Boolean, default: false },
    /**
     * How the "Add task…" row schedules the created task; defaults to
     * scheduling on `date`. The Week section passes a week-postponing action
     * instead, so the created task lands in the week it displays, not on a
     * day.
     */
    addTaskAction: {
      type: Object as PropType<ConsoleActionTask>,
      required: false,
      default: undefined,
    },
    date: { type: Date, required: true },
    emptyText: { type: String, required: false },
    items: { type: Array as PropType<GeneralizedTaskId[]>, required: true },
    isToday: { type: Boolean, required: true },
    pagination: { type: Number, required: false, default: undefined },
    taskActions: { type: Object as PropType<TaskActions>, required: false },
    hideScheduledDate: { type: Boolean, default: false },
    /** Shared ranking key; omit for Overdue / Upcoming (controller order). */
    listKey: { type: String, required: false, default: undefined },
    /** When false, disable handle reorder (e.g. external DnD only). */
    allowReorder: { type: Boolean, default: true },
  },
  emits: {
    unselectItems: () => true,
    isActive: (_: boolean) => true,
  },
  setup(props, { emit }) {
    const { items, date, listKey, pagination } = toRefs(props);

    const listKeyRef = computed(() => listKey.value);
    const {
      orderedIdsReadonly,
      commitOrder,
      release: releaseOrdering,
    } = useOrderableTaskList(listKeyRef, items);

    const orderedItems = computed(() =>
      listKey.value ? orderedIdsReadonly.value : items.value
    );

    const currentPage = ref(1);
    const isLoadingMore = ref(false);
    const infiniteScrollTrigger = ref<HTMLElement | null>(null);

    const displayedItems = computed(() => {
      if (pagination.value === undefined) {
        return orderedItems.value;
      }
      const itemsToShow = currentPage.value * pagination.value;
      return orderedItems.value.slice(0, itemsToShow);
    });

    const displayedTaskIds = computed(() => displayedItems.value);

    const showLoadMore = computed(
      () =>
        pagination.value !== undefined &&
        displayedItems.value.length < orderedItems.value.length
    );

    const displayedLastIndex = computed(() => displayedItems.value.length - 1);

    // Smart pagination reset - only reset when list significantly shrinks
    const previousLength = ref(items.value.length);
    watch(items, (newItems, oldItems) => {
      const newLength = newItems.length;
      const oldLength = oldItems?.length || 0;

      if (newLength === 0 || oldLength === 0 || newLength < oldLength * 0.5) {
        currentPage.value = 1;
        isLoadingMore.value = false;
      }

      previousLength.value = newLength;
    });

    const loadMore = async () => {
      if (isLoadingMore.value || !showLoadMore.value) return;

      isLoadingMore.value = true;
      // Simulate loading delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 300));
      currentPage.value++;
      isLoadingMore.value = false;
    };

    const releaser = useReleaser();
    releaser.defer(releaseOrdering);

    // Set up infinite scroll
    onMounted(() => {
      if (pagination.value === undefined) return;

      const loadMoreDebounced = debounce(async () => {
        if (!isLoadingMore.value) {
          await loadMore();
        }
      }, 300);

      const observer = new IntersectionObserver(
        (entries) => {
          const trigger = entries[0];
          if (trigger.isIntersecting && trigger.intersectionRatio >= 0.5) {
            loadMoreDebounced();
          }
        },
        {
          threshold: 0.5,
          rootMargin: "50px",
        }
      );

      if (infiniteScrollTrigger.value) {
        observer.observe(infiniteScrollTrigger.value);
      }

      releaser.defer(() => {
        observer.disconnect();
        loadMoreDebounced.cancel();
      });
    });

    const onReorder = (visibleAfter: string[]) => {
      if (listKey.value) commitOrder(visibleAfter);
    };

    const clock = inject.clock();
    const isInPast = computed(() =>
      isAfter(startOfDay(clock.value), startOfDay(date.value))
    );

    // The host's action, or the default day scheduling. Named apart from the
    // `addTaskAction` prop: setup bindings shadow props in the template.
    const effectiveAddTaskAction = computed<ConsoleActionTask>(
      () =>
        props.addTaskAction ?? {
          type: "task-scheduling",
          date: date.value,
        }
    );

    const taskRowActions = computed<TaskActions>(() => ({
      drag: true,
      open: true,
      postpone: true,
      star: false,
      hierarchy: true,
      schedule: {
        able: true,
        type: "shortest",
      },
      notes: true,
      ...props.taskActions,
    }));

    const consoleModal = inject.consoleModal();
    const controller = inject.controller();
    const contextMenu = inject.contextMenu();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const unselectItems = () => {
      emit("unselectItems");
    };
    const {
      addTaskButtonEl,
      isEditing,
      isLoading,
      lastIndex,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      displayedTaskIds,
      {
        controller,
        console: consoleModal,
        contextMenu,
        isInputFocused,
        isModalShown,
      },
      { unselectItems }
    );
    releaser.defer(releaseTaskList);

    const focusAddTask = () => {
      addTaskButtonEl.value?.focus?.();
    };

    const isActive = ref(false);
    watch(selectedTaskId, (newId) => {
      const wasActive = isActive.value;
      isActive.value = !!newId;
      if (wasActive !== isActive.value) {
        emit("isActive", isActive.value);
      }
    });

    return {
      effectiveAddTaskAction,
      addTaskButtonEl,
      displayedItems,
      displayedLastIndex,
      displayedTaskIds,
      focusAddTask,
      isEditing,
      isInPast,
      isLoading,
      isLoadingMore,
      lastIndex,
      loadMore,
      onReorder,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      showLoadMore,
      taskListEl,
      taskRowActions,
      unselectTask,
      infiniteScrollTrigger,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/itemslist.scss";

.itemslist {
  flex: 1;
  max-width: 100%;
  // Non-reorderable sections (Overdue / Upcoming / …): match the sortable
  // checkbox column (task pad 16 + this 16 = 32). Sortable lists / Add task
  // reserve that column via TaskInline's handle instead.
  padding-inline-start: 16px;

  &.canAddTask,
  &.reorderable {
    padding-inline-start: 0;
  }

  .bottom {
    flex: 1;
  }
}

.add-task {
  &.star {
    margin-inline-start: 32px;
    width: calc(100% - 32px);
  }
  &.reorderable {
    // Match TaskInline reorderable checkbox (8+16+8 = 32px): Add task
    // pad is 16px, so only 16px more margin is needed.
    margin-inline-start: 16px;
    width: calc(100% - 16px);
  }
}

.empty {
  margin-inline-start: 32px;
  margin-top: 10px;
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.infinite-scroll-trigger {
  min-height: 60px;
  margin: 16px 0;
  margin-inline-start: 32px;
  display: flex;
  justify-content: center;
  align-items: center;

  .loading-more {
    display: flex;
    align-items: center;
    gap: 8px;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
